import { useState, useMemo, useEffect } from "react";
import { AlertTriangle, Ban, Package, Barcode, ChevronLeft, ChevronRight, Pizza, Layers, UtensilsCrossed } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import type { Tables } from "@/integrations/supabase/types";
import type { StockAvailability } from "@/hooks/useStockAvailability";
import type { InternalComboWithDetails } from "@/hooks/useCombos";
import { getProductBorderColor } from "./CategoryTabs";

// PDV dark-theme product border colors (left accent)
const PDV_PRODUCT_BORDER_STYLES: Record<string, string> = {
  blue: "border-l-2 border-l-blue-500/60",
  green: "border-l-2 border-l-green-500/60",
  orange: "border-l-2 border-l-orange-500/60",
  purple: "border-l-2 border-l-purple-500/60",
  pink: "border-l-2 border-l-pink-500/60",
  cyan: "border-l-2 border-l-cyan-500/60",
  amber: "border-l-2 border-l-amber-500/60",
  red: "border-l-2 border-l-red-500/60",
  indigo: "border-l-2 border-l-indigo-500/60",
  teal: "border-l-2 border-l-teal-500/60",
  lime: "border-l-2 border-l-lime-500/60",
  rose: "border-l-2 border-l-rose-500/60",
};

type Product = Tables<"products"> & { barcode?: string | null };

interface ProductGridProps {
  products: Product[];
  combos?: InternalComboWithDetails[];
  onProductClick: (product: Product) => void;
  onComboClick?: (combo: InternalComboWithDetails) => void;
  stockAvailability?: Map<string, StockAvailability>;
  blockSalesWithoutStock?: boolean;
  categoryColor?: string | null;
}

const ITEMS_PER_PAGE = 18; // 6 columns x 3 rows

export function ProductGrid({ 
  products, 
  combos = [],
  onProductClick,
  onComboClick,
  stockAvailability,
  blockSalesWithoutStock = false,
  categoryColor,
}: ProductGridProps) {
  const [currentPage, setCurrentPage] = useState(1);

  // Combine products and combos for display
  const allItems = useMemo(() => {
    const productItems = products.map((p) => ({ type: 'product' as const, data: p }));
    const comboItems = combos.map((c) => ({ type: 'combo' as const, data: c }));
    // Show combos first, then products
    return [...comboItems, ...productItems];
  }, [products, combos]);

  // Reset to page 1 when items change (e.g., category filter)
  useEffect(() => {
    setCurrentPage(1);
  }, [allItems.length]);

  const totalPages = Math.ceil(allItems.length / ITEMS_PER_PAGE);
  
  const paginatedItems = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return allItems.slice(start, start + ITEMS_PER_PAGE);
  }, [allItems, currentPage]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  if (allItems.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <Package className="h-12 w-12 text-[var(--pdv-text-subtle,#4a7abf)] opacity-30 mb-3" />
        <p className="text-sm text-[var(--pdv-text-subtle,#4a7abf)]">
          Nenhum produto nesta categoria
        </p>
      </div>
    );
  }

  const handlePreviousPage = () => {
    setCurrentPage((p) => Math.max(1, p - 1));
  };

  const handleNextPage = () => {
    setCurrentPage((p) => Math.min(totalPages, p + 1));
  };

  const getComboIcon = (comboType: string) => {
    switch (comboType) {
      case 'pizza':
        return <Pizza className="h-3 w-3" />;
      case 'rodizio':
        return <UtensilsCrossed className="h-3 w-3" />;
      default:
        return <Layers className="h-3 w-3" />;
    }
  };

  const getComboTypeLabel = (comboType: string) => {
    switch (comboType) {
      case 'pizza':
        return 'Pizza';
      case 'rodizio':
        return 'Rodízio';
      default:
        return 'Combo';
    }
  };

  return (
    <div className="flex flex-col h-full w-full max-w-full">
      <TooltipProvider>
        <div className="flex-1 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 content-start w-full">
          {paginatedItems.map((item) => {
            if (item.type === 'combo') {
              const combo = item.data as InternalComboWithDetails;
              return (
                <button
                  key={`combo-${combo.id}`}
                  onClick={() => onComboClick?.(combo)}
                  className={cn(
                    "flex flex-col rounded-[10px] border bg-[var(--pdv-surface,#111829)] border-[var(--pdv-border,#1e2d52)] p-3 text-left relative min-h-[90px]",
                    "transition-all duration-150 ease-out",
                    "hover:bg-[var(--pdv-highlight-hover,#142040)] hover:border-[var(--pdv-highlight-hover-border,#1a5fa0)] hover:scale-[1.02]",
                    "active:scale-[0.97]",
                    "focus:outline-none focus:ring-2 focus:ring-[var(--pdv-accent)] focus:ring-offset-0",
                    combo.combo_type === 'rodizio' && "border-orange-500/40"
                  )}
                >
                  {/* Combo type badge */}
                  <div className="absolute top-1.5 right-1.5">
                    <Badge 
                      variant="secondary"
                      className={cn(
                        "text-[9px] px-1.5 py-0 h-5 gap-1 rounded-full",
                        combo.combo_type === 'rodizio' 
                          ? "bg-orange-900/60 text-orange-300 border border-orange-600/50"
                          : "bg-[var(--pdv-highlight,#0a3a6e)] text-[var(--pdv-accent-light,#4dd0ff)] border border-[var(--pdv-highlight-hover-border,#1a5fa0)]"
                      )}
                    >
                      {getComboIcon(combo.combo_type)}
                      {getComboTypeLabel(combo.combo_type)}
                    </Badge>
                  </div>

                  {/* Combo info */}
                  <p className="font-bold text-xs text-[var(--pdv-text,#c8dff5)] line-clamp-2 mb-auto pr-14 min-h-[32px]">
                    {combo.name}
                  </p>
                  
                  <div className="mt-2">
                    <p className="text-[15px] font-extrabold text-[var(--pdv-accent,#00aaff)]">
                      {formatCurrency(Number(combo.price))}
                    </p>
                    {combo.combo_type === 'pizza' && combo.pizza_max_flavors && (
                      <p className="text-[10px] text-[var(--pdv-text-subtle,#4a7abf)]">
                        Até {combo.pizza_max_flavors} sabor{combo.pizza_max_flavors > 1 ? 'es' : ''}
                      </p>
                    )}
                  </div>
                </button>
              );
            }

            // Regular product
            const product = item.data as Product;
            const availability = stockAvailability?.get(product.id);
            const hasLowStock = availability && !availability.available;
            const isBlocked = blockSalesWithoutStock && hasLowStock;
            const hasBarcode = !!product.barcode;

            return (
              <Tooltip key={`product-${product.id}`}>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => !isBlocked && onProductClick(product)}
                    disabled={isBlocked}
                    className={cn(
                      "flex flex-col rounded-[10px] border bg-[var(--pdv-surface,#111829)] border-[var(--pdv-border,#1e2d52)] p-3 text-left relative min-h-[90px]",
                      "transition-all duration-150 ease-out",
                      "hover:bg-[var(--pdv-highlight-hover,#142040)] hover:border-[var(--pdv-highlight-hover-border,#1a5fa0)] hover:scale-[1.02]",
                      "active:scale-[0.97]",
                      "focus:outline-none focus:ring-2 focus:ring-[var(--pdv-accent)] focus:ring-offset-0",
                      !product.is_active && "opacity-50",
                      isBlocked && "opacity-50 cursor-not-allowed border-red-800/50 bg-red-950/20",
                      categoryColor && PDV_PRODUCT_BORDER_STYLES[categoryColor] && !isBlocked && PDV_PRODUCT_BORDER_STYLES[categoryColor]
                    )}
                  >
                    {/* Status indicators */}
                    <div className="absolute top-1.5 right-1.5 flex items-center gap-1">
                      {hasBarcode && !isBlocked && !hasLowStock && (
                        <div className="bg-[var(--pdv-highlight,#0a3a6e)] text-[var(--pdv-text-subtle)] rounded-full p-1">
                          <Barcode className="h-2.5 w-2.5" />
                        </div>
                      )}
                      {isBlocked ? (
                        <div className="bg-red-900/60 text-red-300 rounded-full p-1">
                          <Ban className="h-3 w-3" />
                        </div>
                      ) : hasLowStock ? (
                        <div className="bg-amber-900/60 text-amber-300 rounded-full p-1">
                          <AlertTriangle className="h-3 w-3" />
                        </div>
                      ) : null}
                    </div>

                    {/* Product info */}
                    <p className="font-bold text-xs text-[var(--pdv-text,#c8dff5)] line-clamp-2 mb-auto pr-6 min-h-[32px]">
                      {product.name}
                    </p>
                    
                    <div className="mt-2">
                      <p className={cn(
                        "text-[15px] font-extrabold",
                        isBlocked ? "text-[var(--pdv-text-subtle)]" : "text-[var(--pdv-accent,#00aaff)]"
                      )}>
                        {formatCurrency(Number(product.price))}
                      </p>
                      {isBlocked && (
                        <p className="text-[10px] text-red-400">Sem estoque</p>
                      )}
                    </div>
                  </button>
                </TooltipTrigger>
                {hasLowStock && (
                  <TooltipContent side="top" className="max-w-xs bg-[var(--pdv-surface)] border-[var(--pdv-border)]">
                    <div className="text-xs">
                      <p className={cn(
                        "font-semibold mb-1",
                        isBlocked ? "text-red-400" : "text-amber-400"
                      )}>
                        {isBlocked ? "Venda bloqueada - Estoque insuficiente:" : "Estoque insuficiente:"}
                      </p>
                      <ul className="space-y-0.5 text-[var(--pdv-text-muted)]">
                        {availability.insufficientIngredients.map((ing) => (
                          <li key={ing.ingredientId}>
                            {ing.ingredientName}: {ing.available.toFixed(2)}/{ing.required.toFixed(2)} {ing.unit}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </TooltipContent>
                )}
              </Tooltip>
            );
          })}
        </div>
      </TooltipProvider>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 py-3 border-t border-[var(--pdv-border,#1e2d52)] mt-3">
          <Button
            variant="outline"
            size="sm"
            onClick={handlePreviousPage}
            disabled={currentPage === 1}
            className="h-8 px-3 bg-[var(--pdv-surface)] border-[var(--pdv-border)] text-[var(--pdv-text-muted)] hover:bg-[var(--pdv-highlight-hover)] min-h-[44px]"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Anterior
          </Button>
          <span className="text-sm text-[var(--pdv-text-subtle)] px-3">
            Página {currentPage} de {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
            className="h-8 px-3 bg-[var(--pdv-surface)] border-[var(--pdv-border)] text-[var(--pdv-text-muted)] hover:bg-[var(--pdv-highlight-hover)] min-h-[44px]"
          >
            Próxima
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      )}
    </div>
  );
}
